import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, User as UserIcon, Instagram, Menu, X } from "lucide-react";

export default function Nav() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const desktopLink = (to, label, testid) => (
    <Link
      key={to}
      to={to}
      data-testid={testid}
      className={`px-3 py-2 text-sm transition-colors ${
        loc.pathname === to ? "text-[#C6A15B]" : "text-[#A8A196] hover:text-[#EFE8D8]"
      }`}
    >
      {label}
    </Link>
  );

  const mobileLink = (to, label, testid) => (
    <Link
      key={to}
      to={to}
      data-testid={testid}
      className={`block px-4 py-3 text-base border-b border-[rgba(239,232,216,0.06)] transition-colors ${
        loc.pathname === to ? "text-[#C6A15B]" : "text-[#EFE8D8] hover:bg-[#25211B]"
      }`}
    >
      {label}
    </Link>
  );

  const authed = user && user !== false;
  const authedItems = [
    { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
    { to: "/puzzles",   label: "Puzzles",   testid: "nav-puzzles" },
    { to: "/test-elo",  label: "Test Elo",  testid: "nav-test-elo" },
    { to: "/analyze",   label: "Analyze",   testid: "nav-analyze" },
    { to: "/traps",     label: "Traps",     testid: "nav-traps" },
  ];
  const guestItems = [
    { to: "/traps",     label: "Traps",     testid: "nav-traps" },
  ];
  const items = authed ? authedItems : guestItems;

  return (
    <nav className="sticky top-0 z-40 backdrop-blur-xl bg-[#14120F]/85 border-b border-[rgba(239,232,216,0.08)]">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 sm:gap-3 group min-w-0">
          <img
            src="/nav_king.webp"
            alt="Instant Elo"
            data-testid="nav-logo-image"
            width="40"
            height="40"
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover bg-[#14120F] ring-1 ring-[#C6A15B]/50 group-hover:ring-[#C6A15B] transition-all flex-shrink-0"
          />
          <span className="font-serif text-lg sm:text-xl tracking-tight text-[#EFE8D8] whitespace-nowrap">
            Instant Elo
          </span>
        </Link>

        {/* Desktop: inline links (md and up) */}
        <div className="hidden md:flex items-center gap-1 lg:gap-2">
          {items.map((it) => desktopLink(it.to, it.label, it.testid))}
          <a
            href="https://instagram.com/instant_elo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram"
            className="inline-flex items-center text-[#A8A196] hover:text-[#C6A15B] transition-colors px-2 py-2"
            aria-label="@instant_elo on Instagram"
          >
            <Instagram size={16} />
          </a>
          {authed ? (
            <button
              onClick={logout}
              data-testid="nav-logout"
              className="btn-ghost text-xs !py-1.5 !px-3"
              aria-label="Logout"
            >
              <LogOut size={14} />
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login" className="btn-ghost text-sm !py-1.5">
              <UserIcon size={14} className="inline mr-1" /> Sign in
            </Link>
          )}
        </div>

        {/* Mobile: hamburger toggle */}
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-sm text-[#EFE8D8] hover:bg-[#25211B] transition-colors"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile: slide-down panel */}
      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-[rgba(239,232,216,0.08)] bg-[#14120F]/98 backdrop-blur-xl"
        >
          {items.map((it) => mobileLink(it.to, it.label, it.testid))}
          {authed ? (
            <button
              onClick={() => { logout(); setOpen(false); }}
              data-testid="nav-logout-mobile"
              className="w-full text-left flex items-center gap-2 px-4 py-3 text-base text-[#A8A196] hover:text-[#EFE8D8] hover:bg-[#25211B] transition-colors border-b border-[rgba(239,232,216,0.06)]"
            >
              <LogOut size={16} /> Sign out
            </button>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-mobile"
              className="flex items-center gap-2 px-4 py-3 text-base text-[#C6A15B] hover:bg-[#25211B] transition-colors border-b border-[rgba(239,232,216,0.06)]"
            >
              <UserIcon size={16} /> Sign in
            </Link>
          )}
          <a
            href="https://instagram.com/instant_elo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-mobile"
            className="flex items-center gap-2 px-4 py-3 text-base text-[#C6A15B] hover:bg-[#25211B] transition-colors"
          >
            <Instagram size={16} /> @instant_elo on Instagram
          </a>
        </div>
      )}
    </nav>
  );
}
